import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import type { HelpIntent } from '@/components/help/helpIntent'

export type PaletteMode = 'actions' | 'search'

/** Where the dialog came from: the rect it expands out of (0×0 when a
 *  shortcut opened it, which never animates), the control that gets focus back
 *  on close, and any text typed into a header field. A new object per open. */
export interface PaletteSource {
  id: number
  rect: DOMRect
  opener: HTMLElement | null
  query?: string
}

interface CommandPaletteApi {
  /** null = closed. `actions` preselects the Actions category, `search` opens on All. */
  mode: PaletteMode | null
  open: (mode?: PaletteMode, source?: { anchor: HTMLElement; query?: string }) => void
  searchSource: PaletteSource | null
  close: () => void
  /** Switch modes without closing. */
  setMode: (mode: PaletteMode) => void
  /** The dialog's last query. It survives closing (design system), so the
   *  next record search reopens on it. */
  lastQuery: MutableRefObject<string>
  /** Opens the layout's Help surface (drawer) — `?`, the dialog's `help` action
   *  target, Home's setup banner, and the tutorial peek tab. Without an intent
   *  the drawer opens on its index (Get set up first); `{ view: 'tutorial' }`
   *  opens straight onto a page's explanation video. */
  requestHelp: (intent?: HelpIntent) => void
}

const CommandPaletteContext = createContext<CommandPaletteApi | null>(null)

/**
 * True when the event's target is somewhere the user is typing, so a bare
 * printable shortcut (`/`, `?`) must not steal the keystroke. Modifier chords (⌘K)
 * are safe everywhere and skip this check.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  if (el.isContentEditable) return true
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/** A modal dialog (Help, a confirmation, the palette itself) owns the keys, and
 *  so does the guided tour: its bubble is not aria-modal, but ← → step it. */
function modalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]:not([inert] *)') !== null
    || document.querySelector('[data-coach-marks]') !== null
}

/**
 * Owns the single command dialog's open state plus the global shortcuts
 * (shortcuts.ts), so the sidebar's Quick actions / Search pills, the header
 * field, and the keyboard all drive the same overlay, and `?` opens Help.
 * Mounted once in the authenticated layout, inside UpgradeModalProvider — the
 * shell below it opens the upgrade modal (the sidebar's Add organization).
 */
export function CommandPaletteProvider({
  children,
  onHelp,
}: {
  children: React.ReactNode
  /** The layout's opener; receives the caller's intent untouched (the layout
   *  normalises it, so a stray click event still means the index). */
  onHelp: (intent?: HelpIntent) => void
}) {
  const [mode, setModeState] = useState<PaletteMode | null>(null)
  const [searchSource, setSearchSource] = useState<PaletteSource | null>(null)
  const modeRef = useRef<PaletteMode | null>(null)
  modeRef.current = mode
  const sourceId = useRef(0)
  const lastQuery = useRef('')

  const launch = useCallback((next: PaletteMode, source: { anchor: HTMLElement; query?: string } | undefined, glide: boolean) => {
    const active = document.activeElement
    const opener = source?.anchor ?? (active instanceof HTMLElement && active !== document.body ? active : null)
    // Like the design system, the panel grows out of the persistent header
    // field for every pointer entry point (sidebar pills) unless a specific
    // control launched it. Shortcuts never animate (Motion: keyboard), so they
    // pass no rect; an unrendered origin (0×0) opens without motion too.
    const origin = source?.anchor ?? document.querySelector<HTMLElement>('[data-search-anchor]') ?? opener
    const rect = glide ? origin?.getBoundingClientRect() ?? new DOMRect() : new DOMRect()
    setSearchSource({ id: ++sourceId.current, rect, opener, query: source?.query })
    setModeState(next)
  }, [])
  const open = useCallback(
    (next: PaletteMode = 'actions', source?: { anchor: HTMLElement; query?: string }) => launch(next, source, true),
    [launch],
  )
  const close = useCallback(() => setModeState(null), [])
  const setMode = useCallback((next: PaletteMode) => setModeState(next), [])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      // ⌘K / Ctrl+K toggles the dialog from anywhere, including inputs.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (modeRef.current) close()
        else launch('actions', undefined, false)
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      // Bare `/` jumps straight to record search — only when not typing, and
      // never while the dialog is already open (its own input owns the key).
      if (e.key === '/') {
        if (modeRef.current) return
        e.preventDefault()
        e.stopPropagation()
        launch('search', undefined, false)
      } else if (e.key === '?' && !modeRef.current && !modalOpen()) {
        // `?` opens Help's index, unless a dialog already owns the keyboard.
        e.preventDefault()
        e.stopPropagation()
        onHelp()
      }
    }
    // Capture phase, so a focused header field's type-to-search never also
    // takes the `/` or `?` as the first letter of a query.
    document.addEventListener('keydown', handleKey, true)
    return () => document.removeEventListener('keydown', handleKey, true)
  }, [launch, close, onHelp])

  const value = useMemo<CommandPaletteApi>(
    () => ({ mode, open, close, setMode, searchSource, lastQuery, requestHelp: onHelp }),
    [mode, open, close, setMode, searchSource, onHelp],
  )

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
}

export function useCommandPalette(): CommandPaletteApi {
  const ctx = useContext(CommandPaletteContext)
  if (!ctx) {
    throw new Error('useCommandPalette must be used inside a CommandPaletteProvider')
  }
  return ctx
}
